import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Gift, ImagePlus, Loader2, X } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png"];
// Loose PH mobile-format validation: +63 followed by 10 digits (or 09 + 9 digits).
const PH_PHONE_RE = /^(\+639\d{9}|09\d{9})$/;
const REFERRAL_RE = /^MB-[A-Z2-9]{4,10}$/i;

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: "",
    prc_license_number: "",
    referral_code: "",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prefill referral from ?ref= query param.
  useEffect(() => {
    const q = searchParams.get("ref");
    if (q) {
      setForm((prev) => ({ ...prev, referral_code: q.trim().toUpperCase() }));
    }
  }, [searchParams]);

  // Capture ?utm_source= for channel attribution. Sanitized to
  // lowercase [a-z0-9_-], max 32 chars — matches the backend sanitizer.
  const signupSource = useMemo(() => {
    const raw = searchParams.get("utm_source");
    if (!raw) return null;
    const cleaned = raw.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 32);
    return cleaned || null;
  }, [searchParams]);

  const phoneValid = useMemo(
    () => PH_PHONE_RE.test(form.phone.replace(/\s+/g, "")),
    [form.phone]
  );

  const referralValidShape = useMemo(() => {
    if (!form.referral_code) return true; // optional field
    return REFERRAL_RE.test(form.referral_code.trim());
  }, [form.referral_code]);

  const canSubmit = useMemo(
    () =>
      !!form.full_name &&
      !!form.email &&
      form.password.length >= 8 &&
      !!form.prc_license_number &&
      phoneValid &&
      referralValidShape &&
      !!file &&
      !submitting,
    [form, file, submitting, phoneValid, referralValidShape]
  );

  const handleChange = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const acceptFile = (f) => {
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setError("Please upload a JPG or PNG image of your PRC ID.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("PRC ID image must be 5 MB or smaller.");
      return;
    }
    setError("");
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const handleFileInput = (e) => acceptFile(e.target.files?.[0]);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };
  const clearFile = () => {
    setFile(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const normalizePhone = (v) => {
    const trimmed = v.replace(/\s+/g, "");
    if (trimmed.startsWith("09") && trimmed.length === 11) {
      return "+63" + trimmed.slice(1);
    }
    return trimmed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);

    const fd = new FormData();
    fd.append("email", form.email.trim());
    fd.append("password", form.password);
    fd.append("full_name", form.full_name.trim());
    fd.append("phone", normalizePhone(form.phone));
    fd.append("prc_license_number", form.prc_license_number.trim());
    fd.append("prc_id_image", file);
    if (form.referral_code) {
      fd.append("referral_code", form.referral_code.trim().toUpperCase());
    }
    if (signupSource) {
      fd.append("signup_source", signupSource);
    }

    const result = await register(fd);
    setSubmitting(false);

    if (!result.ok) {
      const map = {
        EMAIL_ALREADY_REGISTERED: "An account with that email already exists.",
        INVALID_PRC_LICENSE:
          "That PRC license number doesn't look right. Use letters, digits or dashes (4–20 chars).",
        INVALID_IMAGE_TYPE: "Please upload a JPG or PNG image.",
        FILE_TOO_LARGE: "PRC ID image must be 5 MB or smaller.",
        EMPTY_FILE: "Please attach your PRC ID image.",
        INVALID_REFERRAL_CODE:
          "We couldn't find that referral code. Double-check with your invitee.",
      };
      setError(map[result.error] || result.error);
      return;
    }

    navigate("/verification-pending", { replace: true });
  };

  return (
    <div className="mb-page min-h-screen">
      <div className="mx-auto max-w-2xl px-6 py-12 md:py-20">
        <Link to="/login" className="mb-focus-ring mb-10 inline-flex items-center rounded-lg">
          <BrandLogo className="h-10" />
        </Link>

        <div className="mb-rise">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Create your broker account.
          </h1>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-gray-500">
            MatchBroker is a verified network. We&apos;ll review your PRC license
            before your account is activated.
          </p>
        </div>

        <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-mb-card">
          <GoogleSignInButton testId="register-google-button" />
          <p className="mt-2 text-xs text-gray-500">
            We&apos;ll ask for your PRC license &amp; ID right after Google
            confirms your email.
          </p>
        </div>

        <div className="relative mt-6" data-testid="register-divider">
          <div className="absolute inset-0 flex items-center">
            <span className="h-px w-full bg-gray-100" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
              or use your email
            </span>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-10 rounded-2xl border border-gray-200 bg-white p-8 shadow-mb-card sm:p-10"
          data-testid="register-form"
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="reg-fullname">Full legal name</Label>
              <Input
                id="reg-fullname"
                type="text"
                required
                value={form.full_name}
                onChange={handleChange("full_name")}
                placeholder="Juan Dela Cruz"
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white text-base"
                data-testid="register-fullname-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-email">Email</Label>
              <Input
                id="reg-email"
                type="email"
                required
                value={form.email}
                onChange={handleChange("email")}
                placeholder="you@brokerage.ph"
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white text-base"
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-phone">Mobile number (+63)</Label>
              <Input
                id="reg-phone"
                type="tel"
                required
                value={form.phone}
                onChange={handleChange("phone")}
                placeholder="+639171234567"
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white text-base"
                data-testid="register-phone-input"
                aria-invalid={form.phone.length > 0 && !phoneValid}
              />
              {form.phone.length > 0 && !phoneValid && (
                <p
                  className="text-xs font-medium text-red-600"
                  data-testid="register-phone-error"
                >
                  Use PH format — +63 9XX XXX XXXX or 09XX XXX XXXX.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-license">PRC license number</Label>
              <Input
                id="reg-license"
                type="text"
                required
                value={form.prc_license_number}
                onChange={handleChange("prc_license_number")}
                placeholder="0012345"
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white text-base"
                data-testid="register-license-input"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="reg-password">Password</Label>
              <Input
                id="reg-password"
                type="password"
                minLength={8}
                required
                value={form.password}
                onChange={handleChange("password")}
                placeholder="At least 8 characters"
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white text-base"
                data-testid="register-password-input"
              />
              <p className="text-xs text-gray-500">
                Use 8+ characters. Mix letters, numbers and symbols for a strong password.
              </p>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="reg-referral" className="flex items-center gap-2">
                <Gift className="h-3.5 w-3.5 text-brand-green" />
                Referral code
                <span className="text-xs font-normal text-gray-400">optional</span>
              </Label>
              <Input
                id="reg-referral"
                type="text"
                value={form.referral_code}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    referral_code: e.target.value.toUpperCase(),
                  }))
                }
                placeholder="MB-XXXXXX"
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white font-mono text-base uppercase"
                data-testid="register-referral-input"
                aria-invalid={form.referral_code.length > 0 && !referralValidShape}
              />
              {form.referral_code.length > 0 && !referralValidShape && (
                <p
                  className="text-xs font-medium text-red-600"
                  data-testid="register-referral-error"
                >
                  Referral codes look like <span className="font-mono">MB-XXXXXX</span>.
                </p>
              )}
              {referralValidShape && form.referral_code && (
                <p className="text-xs text-brand-green">
                  Your inviter earns +30 trial days once you&apos;re verified.
                </p>
              )}
            </div>
          </div>

          <div className="mt-8">
            <Label className="mb-2 block">PRC ID image</Label>
            <input
              ref={fileRef}
              id="reg-prcid"
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleFileInput}
              className="sr-only"
              data-testid="register-prcid-input"
            />
            {!preview ? (
              <label
                htmlFor="reg-prcid"
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                className={`mb-transition flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center ${
                  dragging
                    ? "scale-[1.005] border-brand-navy bg-brand-navy-soft"
                    : "border-gray-200 bg-gray-50/70 hover:border-gray-300 hover:bg-white"
                }`}
                data-testid="register-prcid-dropzone"
              >
                <ImagePlus
                  className={`mb-3 h-6 w-6 mb-transition ${
                    dragging ? "text-brand-navy" : "text-gray-400"
                  }`}
                />
                <div className="text-sm font-semibold text-gray-900">
                  Drop your PRC ID here, or click to upload
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  JPG or PNG · Max 5 MB · Kept private, only visible to admins
                </div>
              </label>
            ) : (
              <div className="mb-transition flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4 animate-mb-fade-in">
                <img
                  src={preview}
                  alt="PRC ID preview"
                  className="h-20 w-20 flex-none rounded-lg border border-gray-200 object-cover"
                  data-testid="register-prcid-preview"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-gray-900">
                    {file?.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {(file?.size / 1024).toFixed(0)} KB · Ready to upload
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearFile}
                  className="mb-transition flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-100"
                  aria-label="Remove image"
                  data-testid="register-prcid-remove"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {error && (
            <div
              className="mt-6 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
              data-testid="register-error"
            >
              {error}
            </div>
          )}

          {/* Legal fine-print — required by Google OAuth consent-screen
              review AND for RA 10173 informed-consent. Placed directly
              above the primary CTA so it's unmissable. */}
          <p
            className="mt-6 text-xs leading-relaxed text-gray-500"
            data-testid="register-legal-fine-print"
          >
            By applying, you agree to our{" "}
            <Link
              to="/terms"
              className="font-medium text-brand-navy hover:underline"
              data-testid="register-terms-link"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy"
              className="font-medium text-brand-navy hover:underline"
              data-testid="register-privacy-link"
            >
              Privacy Policy
            </Link>
            .
          </p>

          <div className="mt-8 flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Link
              to="/login"
              className="text-sm font-semibold text-gray-500 hover:text-gray-900"
              data-testid="register-back-to-login"
            >
              Already have an account? Sign in
            </Link>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="mb-focus-ring h-12 rounded-xl bg-[#1B2B4B] px-8 text-base font-semibold text-white shadow-sm transition hover:bg-[#14213A] hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="register-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Submit for verification
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
